import React, { useRef, useState } from "react";
import { Button, Confirm, Icon } from "semantic-ui-react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnergyPerformanceInstallationform } from "../../../../api";
import { motor, useEnergia, useSoloLectura } from "../engine";
import { Notice, Section } from "../common/SectionCommon";
import "../common/common.scss";
import "./SustemiaConnection.scss";

const energyPerformanceCtrl = new EnergyPerformanceInstallationform();
const MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const FORMATOS = ".json,.csv,.txt,.xlsx";

// Datos de la instalación para la plantilla precargada: la instalación recalculada o, si todavía no pasa la
// validación, lo guardado tal cual.
function instalacionParaPlantilla({ f, inst }) {
  if (f) return f;
  if (!inst) return null;
  return { ...inst, _monthly: inst.consumos_mensuales || [] };
}

function ResumenVistaPrevia({ preview, t }) {
  const filas = Object.entries(preview.listas);
  return (
    <div className="ep-connection__preview">
      <p>
        <strong>{preview.modo === "crear" ? t("energy_performance_import_mode_create") : t("energy_performance_import_mode_update")}</strong>{" "}
        «{preview.nombre}» ({preview.id})
      </p>
      {preview.fichaModificada.length > 0 && (
        <p className="ep-section__note">{t("energy_performance_import_profile_changes")}: {preview.fichaModificada.join(", ")}</p>
      )}
      <div className="ep-table">
        <table>
          <thead>
            <tr>
              <th>{t("energy_performance_import_list")}</th>
              <th>{t("energy_performance_import_added")}</th>
              <th>{t("energy_performance_import_changed")}</th>
              <th>{t("energy_performance_import_removed")}</th>
              <th>{t("energy_performance_import_unchanged")}</th>
            </tr>
          </thead>
          <tbody>
            {filas.map(([lista, v]) => (
              <tr key={lista}>
                <td>{lista}</td>
                {["agregadas", "modificadas", "eliminadas"].map((k) => (
                  <td key={k} title={v[k].join("\n")} className={v[k].length && k === "eliminadas" ? "ep-connection__removed" : undefined}>
                    {v[k].length || "—"}
                  </td>
                ))}
                <td>{v.sinCambios || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filas.some(([, v]) => v.eliminadas.length) && (
        <Notice titulo={t("energy_performance_import_removed_title")}>
          {filas.filter(([, v]) => v.eliminadas.length).map(([lista, v]) => (
            <div key={lista}>{lista}: {v.eliminadas.map((x) => x.replace(/^\|/, "").replace("|", " · ")).join("; ")}</div>
          ))}
        </Notice>
      )}
    </div>
  );
}

// Conexión Sustemia (cargar datos) — hoja 19: plantilla del modelo, importación JSON / CSV / .xlsx con vista previa
// y confirmación, y exportación sustemia.energia.v2.
export function SustemiaConnection({ installation, siteSelected, onSaved }) {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const vista = useEnergia(installation);
  const inputRef = useRef(null);
  const [archivo, setArchivo] = useState(null);
  const [revision, setRevision] = useState(null); // { ok, errors, preview, target }
  const [cargando, setCargando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const site = siteSelected || user?.site;
  // un ejemplo o una copia para practicar no reciben importaciones: el archivo va a la planta del sitio
  const soloLectura = useSoloLectura();
  const sinPermiso = soloLectura === "permiso";
  const sinImportar = (soloLectura && !sinPermiso) || !!installation?.practiceOf;

  const formData = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("site", site);
    return fd;
  };

  const revisar = async (file) => {
    setArchivo(file);
    setRevision(null);
    if (!file) return;
    setCargando(true);
    try {
      setRevision(await energyPerformanceCtrl.previewImport(accessToken, formData(file)));
    } catch (error) {
      setRevision({ ok: false, errors: error?.errors || [{ seccion: "Archivo", mensaje: error?.message || t("server_connection_error") }] });
    } finally {
      setCargando(false);
    }
  };

  const importar = async () => {
    setConfirmar(false);
    setCargando(true);
    try {
      const res = await energyPerformanceCtrl.confirmImport(accessToken, formData(archivo));
      toast.success(t("energy_performance_import_done"));
      setArchivo(null);
      setRevision(null);
      if (inputRef.current) inputRef.current.value = "";
      onSaved?.(res.installation);
    } catch (error) {
      if (Array.isArray(error?.errors)) setRevision({ ok: false, errors: error.errors });
      else toast.error(error?.message || t("server_connection_error"));
    } finally {
      setCargando(false);
    }
  };

  const descargarPlantilla = (vacia) => {
    const datos = motor.plantillaXlsx(vacia ? null : instalacionParaPlantilla(vista), vacia);
    const nombre = vacia ? "vacio" : String(installation.code || "instalacion").replace(/[^A-Za-z0-9_-]/g, "_");
    saveAs(new Blob([datos], { type: MIME_XLSX }), `Sustemia_Modelo_Relevamiento_${nombre}.xlsx`);
  };

  const exportarJson = () => {
    const salida = vista.f ? motor.exportarArchivo(vista.f) : { ...vista.archivo, motor_version: motor.MOTOR_VERSION };
    const nombre = String(installation.code || "instalacion").replace(/[^A-Za-z0-9_-]/g, "_");
    saveAs(new Blob([JSON.stringify(salida, null, 2)], { type: "application/json" }), `${nombre}.sustemia.energia.v2.json`);
  };

  return (
    <div className="ep-screen ep-connection">
      <h3 className="ep-screen__title">{t("energy_performance_connection_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_connection_intro")}</p>

      <Section title={t("energy_performance_template_title")} note={t("energy_performance_template_note")}>
        <div className="ep-connection__buttons">
          {installation && (
            <Button type="button" basic onClick={() => descargarPlantilla(false)}>
              <Icon name="file excel outline" /> {t("energy_performance_template_filled")}
            </Button>
          )}
          <Button type="button" basic onClick={() => descargarPlantilla(true)}>
            <Icon name="file outline" /> {t("energy_performance_template_empty")}
          </Button>
        </div>
      </Section>

      <Section title={t("energy_performance_import_title")} note={t("energy_performance_import_note")}>
        {sinImportar && <Notice>{t("energy_performance_import_not_here")}</Notice>}
        {!sinImportar && sinPermiso && <Notice>{t("energy_performance_no_permission_import")}</Notice>}
        {!sinImportar && <label className="ep-connection__file">
          <span>{t("energy_performance_import_choose")}</span>
          <input ref={inputRef} type="file" accept={FORMATOS} aria-label={t("energy_performance_import_choose")} disabled={cargando || !site || sinPermiso} onChange={(e) => revisar(e.target.files && e.target.files[0])} />
        </label>}
        {!site && <Notice>{t("select_company_site_from_header")}</Notice>}
        {cargando && <p className="ep-section__note">{t("energy_performance_import_checking")}</p>}

        {revision && !revision.ok && (
          <div className="ep-connection__errors" role="alert">
            <Notice tipo="danger" titulo={`${t("energy_performance_import_errors_title")} (${(revision.errors || []).length})`}>
              {t("energy_performance_import_errors_note")}
            </Notice>
            <div className="ep-table">
              <table>
                <thead>
                  <tr>
                    <th>{t("energy_performance_import_section")}</th>
                    <th>{t("energy_performance_row")}</th>
                    <th>{t("energy_performance_import_line")}</th>
                    <th>{t("energy_performance_import_problem")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(revision.errors || []).map((e, i) => (
                    <tr key={i}>
                      <td>{e.seccion}</td>
                      <td>{e.fila ?? "—"}</td>
                      <td>{e.linea ?? "—"}</td>
                      <td>{e.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {revision && revision.ok && (
          <>
            <ResumenVistaPrevia preview={revision.preview} t={t} />
            <div className="ep-savebar">
              <Button type="button" basic onClick={() => revisar(null)}>{t("cancel")}</Button>
              <Button type="button" primary loading={cargando} disabled={cargando}
                onClick={() => (revision.target.mode === "actualizar" ? setConfirmar(true) : importar())}>
                <Icon name="upload" /> {t("energy_performance_import_confirm")}
              </Button>
            </div>
          </>
        )}
      </Section>

      {installation && (
        <Section title={t("energy_performance_export_title")} note={t("energy_performance_export_note")}>
          <Button type="button" basic onClick={exportarJson}>
            <Icon name="download" /> {t("energy_performance_export_json")}
          </Button>
          {vista.error && <Notice>{t("energy_performance_export_unvalidated")}: {vista.error}</Notice>}
        </Section>
      )}

      <Confirm
        open={confirmar}
        content={t("energy_performance_import_confirm_update")}
        cancelButton={t("cancel")}
        confirmButton={t("energy_performance_import_confirm")}
        onCancel={() => setConfirmar(false)}
        onConfirm={importar}
      />
    </div>
  );
}
